import { Check, Circle, Lock, AlertCircle, GitBranch } from 'lucide-react';
import clsx from 'clsx';
import type { StepId, StepStatus, TuningSession } from '../types';
import { STEP_REGISTRY } from '../steps/registry';

interface SidebarProps {
  session: TuningSession;
  /** Currently active step (mirrors session.currentStep but kept explicit for clarity). */
  activeStep: StepId;
  onStepClick: (step: StepId) => void;
}

/**
 * Persistent left-rail checklist. Clickable any time — users can jump back to
 * earlier steps to re-check or update values.
 *
 * Branch step (`rest_branch`) shows the chosen branch's child step indented;
 * the unchosen branch is hidden from the list (but its data is preserved in
 * session.stepData in case the user switches back).
 */
export function Sidebar({ session, activeStep, onStepClick }: SidebarProps) {
  const restType = session.branches.restType;

  // Filter the registry to the steps that should appear given the current
  // branch selection. Both rest sub-steps are hidden until the user picks;
  // once picked, only the chosen branch's step shows.
  const visibleSteps = STEP_REGISTRY.filter((s) => {
    if (s.id === 'rest_limb_driven') return restType === 'limb_driven';
    if (s.id === 'rest_cable_driven') return restType === 'cable_driven';
    return true;
  });

  return (
    <aside className="w-full lg:w-64 lg:shrink-0 border-r border-zinc-800 bg-zinc-950/60 lg:h-[calc(100vh-160px)] lg:sticky lg:top-0 overflow-y-auto">
      <div className="p-4 border-b border-zinc-800/60">
        <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Tuning session</p>
        <h2 className="text-sm font-semibold mt-1">Bow Tuning Wizard</h2>
        <p className="text-[11px] text-zinc-500 mt-2 leading-snug">
          Step-by-step from "out of the box" to "ready to hunt." Click any step to jump to it.
        </p>
      </div>

      <ol className="py-2">
        {visibleSteps.map((step, i) => {
          const status: StepStatus = getStepStatus(step.id, session);
          const isActive = step.id === activeStep;
          const isBranchChild = step.id === 'rest_limb_driven' || step.id === 'rest_cable_driven';

          return (
            <li key={step.id}>
              <button
                onClick={() => onStepClick(step.id)}
                className={clsx(
                  'w-full text-left flex items-start gap-3 px-4 py-2.5 transition border-l-2',
                  isBranchChild && 'pl-9',
                  isActive
                    ? 'border-ember-500 bg-ember-500/5'
                    : 'border-transparent hover:bg-zinc-900/50',
                )}
              >
                <StatusIcon status={status} branched={step.id === 'rest_branch' && !!restType} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10px] font-mono text-zinc-500">
                      {String(stepNumberOf(step.id, visibleSteps, i)).padStart(2, '0')}
                    </span>
                    <span
                      className={clsx(
                        'text-sm font-medium truncate',
                        isActive ? 'text-ember-500' : status === 'complete' ? 'text-zinc-300' : 'text-zinc-400',
                      )}
                    >
                      {step.title}
                    </span>
                  </div>
                  {step.id === 'rest_branch' && restType && (
                    <p className="text-[10px] text-zinc-500 mt-0.5">
                      {restType === 'limb_driven' ? 'Limb-driven path' : 'Cable-driven path'}
                    </p>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ol>

      <ProgressFooter session={session} totalSteps={visibleSteps.length} />
    </aside>
  );
}

function StatusIcon({ status, branched }: { status: StepStatus; branched: boolean }) {
  const cls = 'shrink-0 mt-0.5';
  if (branched) return <GitBranch size={14} className={`${cls} text-emerald-400`} />;
  if (status === 'complete') return <Check size={14} className={`${cls} text-emerald-400`} />;
  if (status === 'in_progress') return <Circle size={14} className={`${cls} text-ember-500 fill-ember-500/30`} />;
  if (status === 'needs_attention') return <AlertCircle size={14} className={`${cls} text-amber-400`} />;
  if (status === 'locked') return <Lock size={12} className={`${cls} text-zinc-700`} />;
  return <Circle size={14} className={`${cls} text-zinc-700`} />;
}

function getStepStatus(stepId: StepId, session: TuningSession): StepStatus {
  if (session.completedSteps.includes(stepId)) return 'complete';
  if (session.stepData[stepId]) return 'in_progress';
  return 'untouched';
}

/** Display-friendly step number (1-indexed), accounting for branched-out steps. */
function stepNumberOf(_stepId: StepId, _visible: typeof STEP_REGISTRY, index: number): number {
  return index + 1;
}

function ProgressFooter({ session, totalSteps }: { session: TuningSession; totalSteps: number }) {
  const done = session.completedSteps.length;
  const pct = totalSteps > 0 ? Math.round((done / totalSteps) * 100) : 0;
  return (
    <div className="p-4 border-t border-zinc-800/60 mt-2">
      <div className="flex items-baseline justify-between text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
        <span>Progress</span>
        <span>{done}/{totalSteps}</span>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-ember-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
